import { Injectable } from "@angular/core";
import { PreferencesService } from "core/preferences.service";
import { ProcessResource, ProcessVersionsManagementResource } from "core/stages-client";
import { AbstractControl, AsyncValidatorFn, FormGroup, ValidationErrors } from "@angular/forms";
import { Observable, of, timer } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { FormService } from "common/form/form.service";

type ProcessVersion = stages.process.ProcessVersion;
type CompareBaselines = stages.process.CompareBaselines;
type JobIdentifier = stages.core.scheduler.JobIdentifier;
export interface ProcessVersionWithAvailability extends ProcessVersion {
	unavailable?: boolean;
}

export interface WorkflowStartErrorResponse {
	entityName: string;
	fieldNames: string;
	validationMessage: string[];
	validatorName: string;
}

@Injectable({ providedIn: "root" })
export class ProcessVersionService {
	constructor(
		private readonly processResource: ProcessResource,
		private readonly processVersionsManagementResource: ProcessVersionsManagementResource,
		private readonly preferencesService: PreferencesService,
		private readonly formService: FormService,
	) {}

	async getProcessVersions(
		currentWorkspaceId: string,
		suppressTypeCheck: boolean,
		pv?: string,
		type?: string,
		identity?: string,
	): Promise<ProcessVersionWithAvailability[]> {
		if (!suppressTypeCheck && type && identity && pv) {
			return this.getProcessVersionsForElement(currentWorkspaceId, pv, type, identity);
		}
		return this.processResource.getProcessVersions$GET$workspace_workspaceId_process_versions(currentWorkspaceId);
	}

	async getCompareBaselines(
		currentWorkspaceId: string,
		compareWorkspaceId: string,
		pv: string,
	): Promise<CompareBaselines> {
		return this.processResource.getCompareBaselines(currentWorkspaceId, {
			compareWorkspaceId: compareWorkspaceId,
			pv: pv,
		});
	}

	async diff(
		currentWorkspaceId: string,
		pv: string,
		targetWorkspaceId: string,
		targetProcessVersion: string,
	): Promise<JobIdentifier> {
		this.processVersionsManagementResource.setAdditionalHttpParamsForNextRequest({
			pv: pv,
		});
		return this.processVersionsManagementResource.diff(currentWorkspaceId, targetWorkspaceId, targetProcessVersion, {
			pv: pv,
		});
	}

	private async getProcessVersionsForElement(
		currentWorkspaceId: string,
		pv: string,
		type: string,
		identity: string,
	): Promise<ProcessVersionWithAvailability[]> {
		return this.processResource
			.getProcessVersions$GET$workspace_workspaceId_process_versions_type_identity(currentWorkspaceId, type, identity)
			.then((processVersions) => {
				const resultList = processVersions.available;
				processVersions.unavailable.forEach((processVersion: ProcessVersionWithAvailability) => {
					processVersion.unavailable = true;
					resultList.push(processVersion);
				});
				return resultList;
			});
	}

	async getInitialProcessVersionIdentifier(): Promise<string> {
		return this.preferencesService
			.getPreference("pv", {
				pv: "_vv",
			})
			.then((storedProcessVersionIdentifierOrDefault) => {
				return storedProcessVersionIdentifierOrDefault.pv;
			});
	}

	async validateProcessVersionName(workspaceId: string, processVersionName: string, form: FormGroup): Promise<void> {
		return this.processVersionsManagementResource.validateProcessVersionName(workspaceId, {
			name: processVersionName,
		});
	}

	processVersionNameValidatorFunction(workspaceId: string, form: FormGroup): AsyncValidatorFn {
		return (control: AbstractControl): Observable<ValidationErrors | null> | Promise<ValidationErrors | null> => {
			if (control.value.length <= 0) {
				return of({ required: true });
			}
			return timer(500).pipe(
				switchMap(async () => this.validateProcessVersionName(workspaceId, control.value, form)),
				map(() => null),
				catchError((e) => {
					const errors = {};
					this.formService.populateValidationErrorsOrThrow(errors, e);
					return of(errors);
				}),
			);
		};
	}
}

export function getTranslateStringForError(error: WorkflowStartErrorResponse): string {
	return "management.process.versions." + error.validatorName;
}
